/** Canonical Canvas node icon assignment helpers.
 *
 * Assignment source of truth: CanvasNodeCatalogEntry.icon_ref (backend).
 * Frontend registries only map icon_ref → visual (builtin component / custom URL).
 *
 * VISTOERA_CANVAS_NODE_CATALOG_ICON_AVAILABILITY_TRUTH_AND_TENANT_GLOBAL_ICON_PROPAGATION_P0_V2
 */

import type { ReactNode } from 'react'
import { FALLBACK_ICON_KEY, NODE_ICON_CATALOG, type NodeIconKey } from './nodeIconCatalog'

let _assignmentByNodeType: Record<string, string> = {}
let _customUrlByRef: Record<string, string> = {}
let _assignmentRevision = 0
const _listeners = new Set<() => void>()

function _bump() {
  _assignmentRevision += 1
  for (const l of _listeners) l()
}

export function getCanvasIconAssignmentRevision(): number {
  return _assignmentRevision
}

export function subscribeCanvasIconAssignments(listener: () => void): () => void {
  _listeners.add(listener)
  return () => {
    _listeners.delete(listener)
  }
}

/** Replace canonical node_type → icon_ref map (from Platform/Tenant catalog). */
export function setCanvasIconAssignments(assignments: Record<string, string>): void {
  _assignmentByNodeType = { ...(assignments || {}) }
  _bump()
}

/** Merge assignments without dropping keys not present in `partial`. */
export function mergeCanvasIconAssignments(partial: Record<string, string>): void {
  _assignmentByNodeType = { ..._assignmentByNodeType, ...(partial || {}) }
  _bump()
}

export function getCanvasIconAssignments(): Record<string, string> {
  return _assignmentByNodeType
}

export function getAssignedIconRef(nodeType: string): string | undefined {
  return _assignmentByNodeType[nodeType]
}

export function setCustomIconUrlMap(urls: Record<string, string>): void {
  _customUrlByRef = { ...(urls || {}) }
  _bump()
}

export function mergeCustomIconUrlMap(partial: Record<string, string>): void {
  _customUrlByRef = { ..._customUrlByRef, ...(partial || {}) }
  _bump()
}

export function getCustomIconUrl(iconRef: string): string | undefined {
  return _customUrlByRef[iconRef]
}

export type IconOccupancyOwner = {
  nodeType: string
  label: string
}

/** Build occupancy from full catalog rows (ONLINE + OFFLINE + NOT_READY). */
export function buildIconOccupancyFromCatalog(
  entries: Array<{ node_type: string; display_name?: string | null; icon_ref: string }>,
): Map<string, IconOccupancyOwner[]> {
  const map = new Map<string, IconOccupancyOwner[]>()
  for (const e of entries) {
    if (!e?.icon_ref || !e.node_type) continue
    const list = map.get(e.icon_ref) || []
    list.push({
      nodeType: e.node_type,
      label: (e.display_name && String(e.display_name).trim()) || e.node_type,
    })
    map.set(e.icon_ref, list)
  }
  return map
}

export type IconPickerStatusKind = 'available' | 'this' | 'other'

export function iconPickerStatusFor(
  iconRef: string,
  occupancy: Map<string, IconOccupancyOwner[]>,
  editingNodeType: string | null | undefined,
): { kind: IconPickerStatusKind; text: string } {
  const owners = occupancy.get(iconRef) || []
  const others = owners.filter((o) => o.nodeType !== editingNodeType)
  const self = owners.some((o) => o.nodeType === editingNodeType)
  if (self && others.length === 0) return { kind: 'this', text: '当前图标' }
  if (others.length > 0) return { kind: 'other', text: `已用于：${others.map((o) => o.label).join('、')}` }
  if (self) return { kind: 'this', text: '当前图标' }
  return { kind: 'available', text: '可用' }
}

/** Resolve icon_ref → React visual (builtin catalog or custom URL img). */
export function resolveCanvasNodeIcon(iconRef: string | undefined | null): ReactNode {
  if (!iconRef) return NODE_ICON_CATALOG[FALLBACK_ICON_KEY].icon
  if (iconRef.startsWith('custom:')) {
    const url = _customUrlByRef[iconRef]
    if (url) {
      return (
        // eslint-disable-next-line jsx-a11y/alt-text -- decorative toolbar/header glyph
        <img src={url} alt="" style={{ width: '1em', height: '1em', objectFit: 'contain', display: 'block' }} />
      )
    }
    return <span style={{ opacity: 0.45, fontSize: '0.85em' }}>◇</span>
  }
  if (iconRef in NODE_ICON_CATALOG) return NODE_ICON_CATALOG[iconRef as NodeIconKey].icon
  return NODE_ICON_CATALOG[FALLBACK_ICON_KEY].icon
}

export function resetCanvasIconAssignmentsForTests(): void {
  _assignmentByNodeType = {}
  _customUrlByRef = {}
  _bump()
}
